import { useEffect, useState } from "react";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import { toast } from "@/lib/toast";
import {
  patchProjectExecutionClass,
  projectExecutionClassBadgeClass,
  type Order,
  type ProjectExecutionClass,
} from "@/lib/projects";

const OPTIONS: { value: ProjectExecutionClass | ""; label: string }[] = [
  { value: "", label: "Keine" },
  { value: "EXC1", label: "EXC1" },
  { value: "EXC2", label: "EXC2" },
];

function normalizeExc(value: string | null | undefined): ProjectExecutionClass | "" {
  const key = String(value || "").trim().toUpperCase();
  return key === "EXC1" || key === "EXC2" ? key : "";
}

export function ProjectExecutionClassControl({
  project,
  canEdit,
  onSaved,
  hideLabel = false,
}: {
  project: Order;
  canEdit: boolean;
  onSaved?: () => void;
  /** Label kommt von außen (z. B. Meta-Zeile). */
  hideLabel?: boolean;
}) {
  const serverValue = normalizeExc(project.execution_class);
  const [value, setValue] = useState<ProjectExecutionClass | "">(serverValue);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setValue(serverValue);
  }, [project.id, serverValue]);

  async function handleChange(next: ProjectExecutionClass | "") {
    if (!canEdit || busy || next === value) return;
    const previous = value;
    setValue(next);
    setBusy(true);
    try {
      await patchProjectExecutionClass(project.id, next || null);
      toast.success(
        next ? `Execution Class auf ${next} gesetzt.` : "Execution Class entfernt.",
      );
      window.dispatchEvent(new Event("auftraege_changed"));
      onSaved?.();
    } catch (err) {
      setValue(previous);
      toast.error(
        err instanceof Error ? err.message : "Execution Class konnte nicht gespeichert werden.",
      );
    } finally {
      setBusy(false);
    }
  }

  if (!canEdit) {
    if (!value) return null;
    return (
      <span className={clsx("text-xs", projectExecutionClassBadgeClass(value))}>{value}</span>
    );
  }

  return (
    <div className="inline-flex max-w-full items-center gap-2">
      {hideLabel ? null : (
        <span className="text-xs font-medium text-label-tertiary">Execution Class</span>
      )}
      <Listbox value={value} onChange={(next) => void handleChange(next)} disabled={busy}>
        <ListboxButton
          aria-label="Execution Class"
          className={clsx(
            "inline-flex items-center gap-1 rounded-lg py-0.5 pl-0.5 pr-1 text-left",
            "transition hover:bg-fill-tertiary",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft",
            "disabled:cursor-wait disabled:opacity-60",
          )}
        >
          {value ? (
            <span className={clsx("text-xs", projectExecutionClassBadgeClass(value))}>{value}</span>
          ) : (
            <span className="px-1 text-sm text-label-secondary">Keine</span>
          )}
          <ChevronUpDownIcon className="h-4 w-4 shrink-0 text-label-tertiary" aria-hidden />
        </ListboxButton>
        <ListboxOptions
          anchor="bottom start"
          className="z-[80] w-44 rounded-xl border border-separator bg-card p-1 shadow-ios-md [--anchor-gap:6px] focus:outline-none"
        >
          {OPTIONS.map((option) => (
            <ListboxOption
              key={option.label}
              value={option.value}
              className="group flex cursor-pointer items-center justify-between gap-2 rounded-lg px-2 py-1.5 data-[focus]:bg-fill-tertiary data-[selected]:bg-fill-quaternary"
            >
              {option.value ? (
                <span className={clsx("text-xs", projectExecutionClassBadgeClass(option.value))}>
                  {option.label}
                </span>
              ) : (
                <span className="px-1 text-sm text-label-secondary">Keine</span>
              )}
              <CheckIcon className="h-4 w-4 text-accent opacity-0 group-data-[selected]:opacity-100" aria-hidden />
            </ListboxOption>
          ))}
        </ListboxOptions>
      </Listbox>
    </div>
  );
}
